"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/pl";
import { InfoIcon, LayersIcon, RotateCcwIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  ACTIVITY_CELL_ATTR,
  ActivityTooltip,
  useActivityTooltip,
} from "./activity-tooltip";

dayjs.locale("pl");

// Etykiety wierszy — wszystkie 7 dni, małą literą z kropką (jak na makiecie).
const WEEKDAY_LABELS = ["pon.", "wt.", "śr.", "czw.", "pt.", "sob.", "niedz."];

const GREEN_LEVEL_CLASS: Record<number, string> = {
  0: "bg-zinc-100",
  1: "bg-emerald-200",
  2: "bg-emerald-400",
  3: "bg-emerald-600",
  4: "bg-emerald-800",
};

// Wariant "teal" — używany na stronie głównej (karta „Aktywność"), zgodny z designem.
const TEAL_LEVEL_CLASS: Record<number, string> = {
  0: "bg-zinc-100",
  1: "bg-teal-200",
  2: "bg-teal-400",
  3: "bg-teal-600",
  4: "bg-teal-800",
};

const BLUE_LEVEL_CLASS: Record<number, string> = {
  0: "bg-zinc-100",
  1: "bg-sky-200",
  2: "bg-sky-400",
  3: "bg-sky-600",
  4: "bg-sky-800",
};

type Source = "attempts" | "sr";

// dayjs "MMM" w pl zwraca np. "sty", "lut" — makieta dokleja kropkę (poza "maj").
const monthLabel = (date: string) => {
  const m = dayjs(date).format("MMM");
  return m === "maj" ? m : `${m}.`;
};

export const ExamLearningActivity = ({
  activity,
  wrapperClassName = "my-12",
  title = "Heatmapa aktywności",
  segmentedTabs = false,
  solvedPalette = "emerald",
}: {
  activity: ExamLearningActivityVM;
  wrapperClassName?: string;
  title?: string;
  // segmentedTabs = przełącznik jak w designie (dokładnie jedno źródło aktywne).
  segmentedTabs?: boolean;
  solvedPalette?: "emerald" | "teal";
}) => {
  const { days, today, hasLearningPlan, windowStart, windowEnd } = activity;

  const solvedLevelClass =
    solvedPalette === "teal" ? TEAL_LEVEL_CLASS : GREEN_LEVEL_CLASS;
  const solvedDotClass =
    solvedPalette === "teal" ? "bg-teal-500" : "bg-emerald-500";
  // Ramka „dziś" = ciemniejszy odcień koloru kafelka (kafelek dnia zawsze z palety solved).
  const todayRingClass =
    solvedPalette === "teal" ? "ring-teal-700" : "ring-emerald-700";
  const todayBorderClass =
    solvedPalette === "teal" ? "border-teal-700" : "border-emerald-700";

  // Spaced Repetition (sr) steruje jednocześnie przeszłymi powtórkami (zielone)
  // oraz zaplanowaną nauką na przyszłość (niebieskie). Podejścia = osobny tryb.
  // W trybie segmentedTabs dokładnie jedno źródło jest aktywne naraz.
  const [enabled, setEnabled] = useState<Record<Source, boolean>>(() =>
    segmentedTabs
      ? { sr: hasLearningPlan, attempts: !hasLearningPlan }
      : { attempts: true, sr: true },
  );

  // Segmented: klik = wybierz TYLKO to źródło (zawsze dokładnie jedno aktywne).
  const onTabClick = (key: Source) =>
    setEnabled({ sr: key === "sr", attempts: key === "attempts" });
  const { hover, onCellEnter, onCellLeave, onCellClick } = useActivityTooltip();

  // Na mobile siatka jest przewijana poziomo — po wejściu centrujemy ją na „dziś",
  // żeby user nie musiał doscrollować. Na desktopie (brak overflow) scrollLeft jest no-opem.
  const scrollRef = useRef<HTMLDivElement>(null);
  const todayColRef = useRef<HTMLDivElement>(null);

  // Drag-to-scroll myszką (touch/trackpad działa natywnie). `moved` blokuje klik/tooltip komórki
  // po przeciągnięciu, żeby przewijanie nie otwierało dymka.
  const drag = useRef({ down: false, startX: 0, startScroll: 0, moved: false });

  const onDragPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el) return;
    drag.current = {
      down: true,
      startX: e.clientX,
      startScroll: el.scrollLeft,
      moved: false,
    };
  };

  const onDragPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || !drag.current.down) return;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 4) drag.current.moved = true;
    el.scrollLeft = drag.current.startScroll - dx;
  };

  const endDrag = () => {
    drag.current.down = false;
  };

  const onDragClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  // Klik pigułki = zaznacz TYLKO ją (odznacz drugą). Klik jedynej aktywnej = przywróć obie.
  const onPillClick = (key: Source) =>
    setEnabled((prev) => {
      const other: Source = key === "sr" ? "attempts" : "sr";
      if (prev[key] && !prev[other]) {
        return { attempts: true, sr: true };
      }
      return { attempts: key === "attempts", sr: key === "sr" };
    });

  // Wybór gotowego setu "solved" policzonego na serwerze (bez liczenia na froncie).
  const solvedSet = useMemo(() => {
    if (enabled.attempts && enabled.sr) return activity.solved.both;
    if (enabled.attempts) return activity.solved.attempts;
    if (enabled.sr) return activity.solved.sr;
    return null;
  }, [enabled.attempts, enabled.sr, activity.solved]);

  const greenLevels = solvedSet?.levels ?? days.map(() => 0);
  const blueLevels = enabled.sr ? activity.planned.levels : days.map(() => 0);

  // Podział na kolumny-tygodnie (windowStart = poniedziałek, windowEnd = niedziela).
  const weeks = useMemo(() => {
    const result: number[][] = [];
    for (let i = 0; i < days.length; i += 7) {
      result.push(
        Array.from({ length: 7 }, (_, r) => i + r).filter(
          (idx) => idx < days.length,
        ),
      );
    }
    return result;
  }, [days]);

  const cellClass = (index: number) => {
    const day = days[index];
    const isFuture = day.date > today;
    const level = isFuture ? blueLevels[index] ?? 0 : greenLevels[index] ?? 0;
    const palette = isFuture ? BLUE_LEVEL_CLASS : solvedLevelClass;
    return palette[level] ?? palette[0];
  };

  useEffect(() => {
    const sc = scrollRef.current;
    const tc = todayColRef.current;
    if (!sc || !tc) return;
    const scRect = sc.getBoundingClientRect();
    const tcRect = tc.getBoundingClientRect();
    const target =
      sc.scrollLeft +
      tcRect.left -
      scRect.left -
      sc.clientWidth / 2 +
      tcRect.width / 2;
    // Wyrównanie do początku kolumny (szerokość kolumny + gap-1 = 4px), żeby na mobile
    // lewa krawędź nie ucinała kolumny w połowie. Kolumny startują od x=0 kontenera scrolla.
    const pitch = tc.offsetWidth + 4;
    sc.scrollLeft = Math.max(0, Math.round(target / pitch) * pitch);
  }, []);

  // Wariant kompaktowy (segmentedTabs, karta na stronie głównej): stały rozmiar kafelka
  // + przewijanie poziome zamiast rozciągania — inaczej przy szerokim oknie kafelki robią się
  // maleńkie „kropki". Domyślnie (statystyki) rozciągamy na całą szerokość na lg+.
  const fillWidth = !segmentedTabs;
  const monthLabelCls = cn(
    "w-7 flex-none whitespace-nowrap text-[11px] text-zinc-400",
    fillWidth && "lg:w-auto lg:min-w-0 lg:flex-1",
  );
  const weekColCls = cn(
    "flex w-7 flex-none flex-col gap-1",
    fillWidth && "lg:w-auto lg:min-w-0 lg:flex-1",
  );
  const scrollCls = cn(
    "no-scrollbar min-w-0 flex-1 cursor-grab overflow-x-auto px-1 select-none active:cursor-grabbing",
    fillWidth && "lg:cursor-default lg:overflow-visible lg:px-0 lg:select-auto",
  );

  return (
    <div className={cn("my-12", wrapperClassName)}>
      {/* Karta z ramką i paddingiem na każdej szerokości — na mobile siatka i tak przewija się
          poziomo, więc padding nie zabiera jej miejsca. */}
      <div className="h-full rounded-2xl border border-zinc-200 bg-white p-4 text-zinc-900 sm:p-6">
        {/* Nagłówek: tytuł + zakres dat | toggle filtry */}
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:flex-wrap sm:items-center">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-lg font-semibold">{title}</span>
            <span className="text-sm text-zinc-400">
              {dayjs(windowStart).format("D MMM")} –{" "}
              {dayjs(windowEnd).format("D MMM YYYY")}
            </span>
          </div>

          {segmentedTabs ? (
            <div className="inline-flex shrink-0 rounded-lg bg-zinc-100 p-1">
              <SegmentedTab
                label="Spaced repetition"
                icon={<RotateCcwIcon size={14} />}
                active={enabled.sr}
                disabled={!hasLearningPlan}
                onClick={() => onTabClick("sr")}
              />
              <SegmentedTab
                label="Podejścia"
                icon={<LayersIcon size={14} />}
                active={enabled.attempts}
                onClick={() => onTabClick("attempts")}
              />
            </div>
          ) : (
            <div className="inline-flex gap-1.5">
              <FilterPill
                label="Spaced Repetition"
                icon={<RotateCcwIcon size={14} />}
                active={enabled.sr}
                disabled={!hasLearningPlan}
                onClick={() => onPillClick("sr")}
              />
              <FilterPill
                label="Egzaminy"
                icon={<LayersIcon size={14} />}
                active={enabled.attempts}
                onClick={() => onPillClick("attempts")}
              />
            </div>
          )}
        </div>

        {/* Siatka — kafelki rozciągnięte na całą szerokość */}
        <div className="mt-6 flex items-stretch gap-2">
          {/* etykiety wierszy */}
          <div className="flex shrink-0 flex-col gap-1 pt-[20px]">
            {WEEKDAY_LABELS.map((label) => (
              <div
                key={label}
                className="flex flex-1 items-center justify-end text-[11px] text-zinc-400"
              >
                {label}
              </div>
            ))}
          </div>

          {/* Poniżej lg siatka nie mieści się ładnie → przewijanie poziome ze stałym
              rozmiarem kafelka. Na lg+ wraca rozciąganie na całą szerokość (flex-1). */}
          <div
            ref={scrollRef}
            className={scrollCls}
            onPointerDown={onDragPointerDown}
            onPointerMove={onDragPointerMove}
            onPointerUp={endDrag}
            onPointerLeave={endDrag}
            onClickCapture={onDragClickCapture}
            onDragStart={(e) => e.preventDefault()}
          >
            {/* etykiety miesięcy */}
            <div className="mb-1 flex gap-1">
              {weeks.map((week, col) => {
                const firstDay = days[week[0]];
                const prevWeek = weeks[col - 1];
                const prevMonth = prevWeek
                  ? dayjs(days[prevWeek[0]].date).month()
                  : -1;
                const month = dayjs(firstDay.date).month();
                return (
                  <div key={col} className={monthLabelCls}>
                    {month !== prevMonth ? monthLabel(firstDay.date) : ""}
                  </div>
                );
              })}
            </div>

            {/* komórki: kolumny=tygodnie, wiersze=dni */}
            <div className="flex gap-1">
              {weeks.map((week, col) => (
                <div
                  key={col}
                  ref={
                    week.some((i) => days[i].isToday) ? todayColRef : undefined
                  }
                  className={weekColCls}
                >
                  {week.map((index) => {
                    const day = days[index];
                    return (
                      <div
                        key={day.date}
                        {...{ [ACTIVITY_CELL_ATTR]: "" }}
                        onMouseEnter={onCellEnter(index)}
                        onMouseLeave={onCellLeave}
                        onClick={onCellClick(index)}
                        className={cn(
                          "aspect-square w-full rounded-[4px]",
                          cellClass(index),
                          day.isExam &&
                            "ring-2 ring-pink-400 ring-offset-1 ring-offset-white",
                          day.isToday &&
                            cn(
                              "ring-2 ring-offset-1 ring-offset-white",
                              todayRingClass,
                            ),
                        )}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Legenda */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4 text-xs text-zinc-500">
          {/* Skala intensywności — najmniej istotna, na 360px ukrywamy (kolory czytelne z siatki). */}
          <div className="flex items-center gap-1.5 max-sm:hidden">
            <span>Mniej</span>
            {[0, 1, 2, 3, 4].map((l) => (
              <span
                key={l}
                // Atrybut jest znacznikiem dla ciemnego motywu (exam-dark.css): próbki legendy
                // muszą jechać tą samą, odwróconą skalą co kafelki, inaczej legenda przestaje
                // opisywać siatkę. Osobny od `data-activity-cell`, bo ten łapie tooltip.
                data-activity-scale=""
                className={cn(
                  "h-[12px] w-[12px] rounded-[3px]",
                  solvedLevelClass[l],
                )}
              />
            ))}
            <span>Więcej</span>
          </div>
          {/* 2 linie: 1) Rozwiązane + Zaplanowane, 2) Egzamin + Dziś. Na sm+ jeden rząd. */}
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-4">
            <div className="flex items-center gap-4">
              <LegendItem className={solvedDotClass} label="Rozwiązane" />
              <div className="flex items-center gap-1.5">
                <LegendItem className="bg-sky-500" label="Zaplanowane" />
                <span
                  data-title="Zaplanowane pytania są prognozowane, mogą się zmienić."
                  className="cursor-help text-zinc-400"
                >
                  <InfoIcon size={14} />
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <LegendItem
                className="border-2 border-pink-400"
                label="Egzamin"
                hollow
              />
              <LegendItem
                className={cn("border-2", todayBorderClass)}
                label="Dziś"
                hollow
              />
            </div>
          </div>
        </div>
      </div>

      {hover && days[hover.index] && (
        <ActivityTooltip
          day={days[hover.index]}
          today={today}
          left={hover.left}
          top={hover.top}
          bottom={hover.bottom}
        />
      )}
    </div>
  );
};

const FilterPill = ({
  label,
  icon,
  active,
  disabled = false,
  onClick,
  className,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  className?: string;
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={cn(
      "flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors sm:gap-1.5 sm:px-3 sm:py-1.5 sm:text-sm",
      active
        ? "border-zinc-200 bg-white text-zinc-900 shadow-sm"
        : "border-transparent text-zinc-500 hover:border-zinc-200",
      disabled && "cursor-not-allowed opacity-40",
      !disabled && !active && "hover:text-zinc-900",
      className,
    )}
  >
    {icon}
    {label}
  </button>
);

const SegmentedTab = ({
  label,
  icon,
  active,
  disabled = false,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={cn(
      "flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors",
      active
        ? "bg-white text-zinc-900 shadow-sm"
        : "text-zinc-500 hover:text-zinc-900",
      disabled && "cursor-not-allowed opacity-40 hover:text-zinc-500",
    )}
  >
    {icon}
    {label}
  </button>
);

const LegendItem = ({
  className,
  label,
  hollow = false,
}: {
  className: string;
  label: string;
  hollow?: boolean;
}) => (
  <div className="flex items-center gap-1.5">
    <span
      className={cn(
        "h-[12px] w-[12px] rounded-[3px]",
        hollow ? "bg-transparent" : "",
        className,
      )}
    />
    <span>{label}</span>
  </div>
);
